import * as React from "react"
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const scrollAreaVariants = cva("relative overflow-hidden", {
  variants: {
    variant: {
      default: "",
      thin: "",
    },
  },
  defaultVariants: {
    variant: "default",
  },
})

const scrollBarVariants = cva(
  "flex touch-none select-none transition-colors",
  {
    variants: {
      variant: {
        default: "",
        thin: "",
      },
      orientation: {
        vertical: "h-full border-l border-l-transparent p-[1px]",
        horizontal: "flex-col border-t border-t-transparent p-[1px]",
      },
    },
    compoundVariants: [
      {
        variant: "default",
        orientation: "vertical",
        class: "w-2.5",
      },
      {
        variant: "default",
        orientation: "horizontal",
        class: "h-2.5",
      },
      {
        variant: "thin",
        orientation: "vertical",
        class: "w-1.5",
      },
      {
        variant: "thin",
        orientation: "horizontal",
        class: "h-1.5",
      },
    ],
    defaultVariants: {
      variant: "default",
      orientation: "vertical",
    },
  }
)

const scrollThumbVariants = cva("relative flex-1 rounded-full", {
  variants: {
    variant: {
      default: "bg-border",
      thin: "bg-muted-foreground/30 hover:bg-muted-foreground/50",
    },
  },
  defaultVariants: {
    variant: "default",
  },
})

interface ScrollAreaProps
  extends React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root>,
    VariantProps<typeof scrollAreaVariants> {}

const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  ScrollAreaProps
>(({ className, variant, children, ...props }, ref) => (
  <ScrollAreaPrimitive.Root
    ref={ref}
    className={cn(scrollAreaVariants({ variant }), className)}
    {...props}
  >
    <ScrollAreaPrimitive.Viewport className="h-full w-full rounded-[inherit]">
      {children}
    </ScrollAreaPrimitive.Viewport>
    <ScrollBar variant={variant} />
    <ScrollAreaPrimitive.Corner />
  </ScrollAreaPrimitive.Root>
))
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName

interface ScrollBarProps
  extends React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
    VariantProps<typeof scrollBarVariants> {}

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  ScrollBarProps
>(({ className, orientation = "vertical", variant, ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      scrollBarVariants({ variant, orientation }),
      className
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb 
      className={cn(scrollThumbVariants({ variant }))}
    />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
))
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName

export { ScrollArea, ScrollBar }
